import React from 'react';
import {css, styled} from 'styled-components';
import {useFormatMsg} from 'utils/useFormatMsg';
import DefaultButton from './DefaultButton';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';

/**
 * 라벨 선택 버튼 컴포넌트의 Props 인터페이스
 * @property {string} [labelText] - 라벨 텍스트
 * @property {any} value - 선택된 값
 * @property {any} [setValue] - 값 설정 함수
 * @property {boolean} [isColumn] - 세로 방향 여부
 * @property {Array<{text: string; value: any}>} options - 옵션 목록
 * @property {React.CSSProperties} [labelStyle] - 라벨 스타일
 * @property {React.CSSProperties} [buttonsWrapStyle] - 버튼 래퍼 스타일
 * @property {React.CSSProperties} [labelInputWrapStyle] - 라벨 입력 래퍼 스타일
 * @property {React.CSSProperties} [buttonStyle] - 버튼 스타일
 * @property {boolean} [errorMode] - 에러 모드 여부
 * @property {boolean} [readOnly] - 읽기 전용 여부
 */
interface ILabelInputProps {
  labelText?: string;
  value: any;
  setValue?: any;
  isColumn?: boolean;
  options: {
    text: string;
    value: any;
  }[];
  labelStyle?: React.CSSProperties;
  buttonsWrapStyle?: React.CSSProperties;
  labelInputWrapStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  errorMode?: boolean;
  readOnly?: boolean;
}

/**
 * 라벨 선택 버튼 컴포넌트
 * 라벨과 함께 여러 버튼 중 하나를 선택할 수 있는 컴포넌트입니다.
 * @param {ILabelInputProps} props - 라벨 선택 버튼 컴포넌트의 props
 * @returns {JSX.Element} 라벨 선택 버튼 컴포넌트
 */
const LabelSelectButton = ({
  labelText,
  value,
  setValue,
  isColumn,
  options,
  labelStyle,
  buttonsWrapStyle,
  labelInputWrapStyle,
  buttonStyle,
  errorMode,
  readOnly,
}: ILabelInputProps) => {
  const label = useFormatMsg(labelText) || labelText;

  /**
   * 버튼 클릭 핸들러
   * 선택된 값을 설정합니다.
   * @param {string} value - 선택된 값
   * @returns {void}
   */
  const onClick = (value: string) => {
    setValue(value);
  };

  return (
    <LabelInputWrap $isColumn={isColumn} style={labelInputWrapStyle}>
      {labelText && (
        <Label style={labelStyle} htmlFor={label} $isColumn={isColumn}>
          {label}
        </Label>
      )}
      <ButtonsWrap
        $isColumn={isColumn}
        style={{
          ...buttonsWrapStyle,
        }}>
        {readOnly && value ? (
          <DefaultButton
            text={options.find(item => item.value === value)?.text || ''}
            buttonStyle={{
              width: '20%',
              height: '100%',
              backgroundColor: colors.darkGray,
              cursor: 'default',
              ...buttonStyle,
            }}
            textStyle={{
              color: colors.white,
            }}
          />
        ) : (
          options.map(item => (
            <DefaultButton
              key={item.value}
              text={item.text}
              onClick={() => !readOnly && onClick(item.value)}
              buttonStyle={{
                width: '20%',
                height: '100%',
                backgroundColor:
                  value === item.value ? colors.darkGray : colors.lighterGray,
                cursor: readOnly ? 'default' : 'pointer',
                ...buttonStyle,
                border: `1px solid ${
                  errorMode ? colors.primary : 'transparent'
                }`,
              }}
              textStyle={{
                color: value === item.value ? colors.white : colors.borderGray,
              }}
            />
          ))
        )}
      </ButtonsWrap>
    </LabelInputWrap>
  );
};

export default LabelSelectButton;

const LabelInputWrap = styled.div<{$isColumn?: boolean}>`
  width: 100%;
  height: max(45px, min(3.5vw, 64px));
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  ${(props: any) =>
    props.$isColumn &&
    css`
      flex-direction: column;
      height: auto;
    `}
`;

const Label = styled.label<{$isColumn?: boolean}>`
  font-size: ${minmax(20)};
  font-weight: 400;
  width: 20%;
  height: 100%;
  padding-right: 2%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  word-break: break-all;

  ${(props: any) =>
    props.$isColumn &&
    css`
      width: 100%;
      margin-bottom: 12px;
    `};
`;

const ButtonsWrap = styled.div<{$isColumn?: boolean; $error?: boolean}>`
  width: 100%;
  height: max(40px, min(3.5vw, 60px));
  display: flex;
  flex-direction: row;
  column-gap: 2%;
  border-radius: max(8px, min(0.5vw, 16px));
  border: ${props => (props.$error ? '1px solid rgb(230, 30, 22)' : '')};

  ${(props: any) =>
    props.$isColumn &&
    css`
      width: 100%;
    `};
`;
